import { useEffect, useState } from 'react';
import { contracts, type LinkedApplicationSummary, type Permission } from '@immo/api-contract';
import { apiClient } from '@/lib/auth/api-client';
import { mayCall } from '@/lib/permissions/route-gate';

/**
 * What the caller knows about a workspace's approved applications.
 *
 * Four outcomes, and they are NOT one empty list. "Still asking", "you may not
 * ask", "asked and the workspace has none" and "the read failed" mean different
 * things to a surface: a page that collapses them shows one indefinite skeleton
 * for a state that will never resolve (BL-058). The navigation row treats every
 * non-`ready` state the same — correctly, since a row must not flicker — but the
 * page does not, so the distinction has to survive the hook.
 */
export type LinkedApplicationsState =
  | { readonly status: 'loading' }
  | { readonly status: 'forbidden' }
  | { readonly status: 'failed' }
  | { readonly status: 'ready'; readonly applications: ReadonlyArray<LinkedApplicationSummary> };

const NOT_PERMITTED: LinkedApplicationsState = { status: 'forbidden' };

/**
 * The linked applications approved for the workspace (IMM-751.4), for the
 * navigation row whose visibility is data rather than a permission, and for the
 * page behind it.
 *
 * The permission is checked BEFORE the request, not after: a member who may not
 * order must not issue a call they would be refused.
 */
export function useLinkedApplications(
  workspaceId: string,
  permissions: ReadonlyArray<Permission>,
  retryAttempt = 0,
): LinkedApplicationsState {
  const permitted = mayCall(permissions, contracts.appHandoffs.listLinkedApplications);
  const [state, setState] = useState<LinkedApplicationsState>(permitted ? { status: 'loading' } : NOT_PERMITTED);

  useEffect(() => {
    if (!permitted) {
      setState(NOT_PERMITTED);

      return;
    }

    // Reset on every change of subject. `Sidebar` is re-rendered rather than
    // remounted when a member switches workspace, so without this the previous
    // workspace's answer survives for one round trip — long enough to show a row
    // the new workspace has no application for.
    setState({ status: 'loading' });

    let cancelled = false;

    void (async () => {
      try {
        const result = await apiClient.appHandoffs.listLinkedApplications({ pathParams: { workspaceId } });

        if (cancelled) return;

        setState(result.ok ? { status: 'ready', applications: result.body.applications } : { status: 'failed' });
      } catch {
        if (!cancelled) setState({ status: 'failed' });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [workspaceId, permitted, retryAttempt]);

  return state;
}

/**
 * The applications this workspace is actually OFFERED — none while the answer is
 * not a `ready` one, and never one whose entry point is hidden (IMM-778.1).
 *
 * The filter lives here rather than at each call site because the navigation row
 * and the page behind it both read through this helper, and two independent
 * filters are exactly how "hidden" comes to mean one thing to the row and
 * another to the page it leads to (BL-082). "Offered", not "approved": once it
 * filters visibility the old name overstates what it returns, and a helper whose
 * name overstates its result is how the two drift apart in the first place.
 */
export function offeredApplications(state: LinkedApplicationsState): ReadonlyArray<LinkedApplicationSummary> {
  return state.status === 'ready' ? state.applications.filter((application) => application.entryPointVisible) : [];
}
